import { SessionStorageWrapper } from '../browser/session-storage-wrapper.ts';
import { LocationWrapper } from '../browser/location-wrapper.ts';
import { Singleton } from '../ioc-container/ioc-container.ts';
import { inject } from 'inversify';
import { LicenseType, User } from './user.ts';
import { Fetch } from '../http/fetch.ts';
import { TokenModel } from './token-model.ts';
import { JwtDecoder } from './jwt.decoder.ts';
import { autorun, makeAutoObservable, runInAction } from 'mobx';

const LOGIN_REDIRECT_URL = '/login/redirect';
const LOGOUT_REDIRECT_URL = '/logout';

const LOGIN_API_URL = '/api/auth/loginUrl';
const LOGOUT_API_URL = '/api/auth/logoutUrl';

const AUTH_TOKEN = 'AUTH_TOKEN';
const REFRESH_TOKEN = 'REFRESH_TOKEN';
const BACK_URL = 'BACK_URL';

const ACCOUNT_ACTIVATION_ROLE = 'account:activated';

const LICENSE_ROLES: Record<string, LicenseType> = {
	'license:test': LicenseType.Test,
	'license:comfort': LicenseType.Comfort,
	'license:compact': LicenseType.Compact,
	'license:basic': LicenseType.Basic,
	'license:exclusive': LicenseType.Exclusive,
};

@Singleton()
export class AuthService {
	private accessToken: string | null = null;
	private refreshToken: string | null = null;
	private refreshTokenTimeout: NodeJS.Timeout | null = null;

	public isRateLimited: boolean = false;

	constructor(
		@inject(Fetch) private fetch: Fetch,
		@inject(SessionStorageWrapper)
		private sessionStorage: SessionStorageWrapper,
		@inject(LocationWrapper) private location: LocationWrapper,
		@inject(JwtDecoder) private tokenService: JwtDecoder,
	) {
		makeAutoObservable(this);
		this.readFromSessionStorage();
		autorun(() => {
			if (this.accessToken == null) {
				this.sessionStorage.clear(AUTH_TOKEN);
			} else {
				this.sessionStorage.set(AUTH_TOKEN, this.accessToken);
			}
		});
		autorun(() => {
			if (this.refreshToken == null) {
				this.sessionStorage.clear(REFRESH_TOKEN);
			} else {
				this.sessionStorage.set(REFRESH_TOKEN, this.refreshToken);
			}
		});
		autorun(() => {
			if (this.accessToken != null) {
				const decodedToken = this.tokenService.decode(this.accessToken);
				const expiry = decodedToken.exp * 1000;
				const now = Date.now();

				if (this.refreshTokenTimeout != null) {
					clearTimeout(this.refreshTokenTimeout);
				}
				this.refreshTokenTimeout = setTimeout(() => this.loginInBackground(), expiry - now);
			}
		});
	}

	/**
	 * Return true if a valid token is found in session.
	 */
	get authenticated(): boolean {
		return this.token != null;
	}

	get accountActivated(): boolean {
		if (this.user?.roles != null) {
			return this.user.roles.includes(ACCOUNT_ACTIVATION_ROLE);
		}

		return false;
	}

	get accountApproved(): boolean {
		return this.user.approved;
	}

	/**
	 * Return the authentication token from the user session.
	 */
	get token(): string | null {
		return this.accessToken;
	}

	get user(): User | null {
		if (this.token == null) {
			return null;
		}

		const decodedToken = this.tokenService.decode(this.token);

		return {
			email: decodedToken.email,
			firstName: decodedToken.given_name,
			lastName: decodedToken.family_name,
			roles: decodedToken.role,
			approved: decodedToken.approved,
			licenseType: this.mapLicense(decodedToken.role),
		};
	}

	/**
	 * Check if the user is authenticated and redirect to log in if not.
	 */
	public async initialize() {
		if (this.token == null && !this.isRedirectUrl()) {
			await this.redirectToLogin();
		}
	}

	public async login() {
		this.clearToken();
		await this.redirectToLogin();
	}

	public async logout() {
		const url = await this.httpGet(LOGOUT_API_URL);
		this.location.navigate(url);
	}

	public async loginInBackground(): Promise<void> {
		const refreshToken = this.sessionStorage.get(REFRESH_TOKEN);
		this.accessToken = await this.httpGet(`/api/auth/refresh?token=${refreshToken}`);
	}

	public getRedirectAfterLoginUrl(): string | null {
		return this.sessionStorage.get(BACK_URL);
	}

	public storeToken(token: TokenModel) {
		this.accessToken = token.accessToken;
		this.refreshToken = token.refreshToken;
	}

	private clearToken(): void {
		this.accessToken = null;
		this.refreshToken = null;
	}

	private isRedirectUrl() {
		return this.location.path === LOGIN_REDIRECT_URL;
	}

	private async redirectToLogin(): Promise<void> {
		this.storeRedirectUrl();
		const url = await this.httpGet(LOGIN_API_URL);
		this.location.navigate(url);
	}

	private storeRedirectUrl() {
		if (this.isLogoutUrl() || this.isRedirectUrl()) {
			return;
		}
		this.sessionStorage.set(BACK_URL, `${this.location.path}${this.location.search}`);
	}

	private isLogoutUrl() {
		return this.location.path === LOGOUT_REDIRECT_URL;
	}

	private async httpGet(url: string): Promise<string> {
		const res = await this.fetch.fetch(url);
		if (!res.ok) {
			throw res;
		}
		const body = await res.json<string>();

		return body;
	}

	private readFromSessionStorage(): void {
		const sessionAccessToken: string | null = this.sessionStorage.get(AUTH_TOKEN) ?? null;
		const sessionRefreshToken: string | null = this.sessionStorage.get(REFRESH_TOKEN) ?? null;
		if (sessionAccessToken !== this.accessToken) {
			runInAction(() => {
				this.accessToken = sessionAccessToken;
			});
		}
		if (sessionRefreshToken !== this.refreshToken) {
			runInAction(() => {
				this.refreshToken = sessionRefreshToken;
			});
		}
	}

	private mapLicense(roles: string[]) {
		const assignedLicenseRole = Object.keys(LICENSE_ROLES).find((license) =>
			roles.includes(license),
		);

		return LICENSE_ROLES[assignedLicenseRole] ?? null;
	}
}